import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  clearStoredSession,
  writeStoredSession,
} from './companyQuestionStorage';
import { TCompanyQuestionSessionHistoryResponse } from './fetchGetCompanyQuestionSessionHistory';
import { postCompanyQuestion } from './fetchPostCompanyQuestion';
import { useGetApplicationQuery } from '../lendingApi/lendingApiQueries';

export function useCompanyQuestionMutation() {
  const { application } = useGetApplicationQuery();
  const queryClient = useQueryClient();
  const applicationId = application?.application_id;

  const queryKey = ['fetchCompanyQuestionSessionHistory', applicationId];

  const persist = (
    session: TCompanyQuestionSessionHistoryResponse | undefined,
  ) => {
    if (!applicationId) return;
    queryClient.setQueryData(queryKey, session);
    if (session) writeStoredSession(applicationId, session);
    else clearStoredSession(applicationId);
  };

  const { mutateAsync, isPending } = useMutation({
    mutationFn: postCompanyQuestion,
    // Persist the answer before the request fires, so a refresh during the
    // in-flight POST (after Continue, before the next question) still recovers
    // it on reload. The pre-submit snapshot is restored if the POST fails.
    onMutate: (variables) => {
      const previous =
        queryClient.getQueryData<TCompanyQuestionSessionHistoryResponse>(
          queryKey,
        );

      persist({
        history: (variables.data.history ?? []).map(({ question, answer }) => ({
          question,
          answer,
        })),
        session_exists: true,
        completed: false,
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      persist(context?.previous);
    },
    onSuccess: (response, variables) => {
      const submittedHistory = variables.data.history ?? [];

      persist({
        history: [
          ...submittedHistory.map(({ question, answer }) => ({
            question,
            answer,
          })),
          ...(response.inference_question
            ? [{ question: response.inference_question, answer: '' }]
            : []),
        ],
        session_exists: true,
        completed: response.questions_complete,
      });
    },
  });

  return {
    postCompanyQuestion: mutateAsync,
    isLoading: isPending,
  };
}
